import { useEffect, useState } from 'react'
import * as S from './InvoicesManagement.styles'
import Modal from 'src/components/Modal'
import { InvoicesTable } from './components/InvoicesTable'
import { useForm } from 'react-hook-form'
import { InvoicesManagementModalSchemaType } from 'src/features/pending-receivements/PendingReceivementsTypes'
import { handleOnKeyDown } from 'src/utils/Form'
import { toast } from 'react-toastify'
import { actions } from '../../../PendingReceivementsState'
import { useDispatch } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import { Box, Typography } from '@mui/material'

interface InvoicesManagementParams {
  receivementId: string
  invoiceKeys: string[]
  badge: string
}

export const InvoicesManagement: React.FC<InvoicesManagementParams> = ({ receivementId, invoiceKeys, badge }) => {
  const [showModal, setShowModal] = useState(false)
  const [invoiceKeysList, setInvoiceKeysList] = useState<string[]>([])
  const dispatch = useDispatch()
  const [isDisabled, setIsDisabled] = useState(false)

  const {
    register,
    formState: { errors },
    getValues,
    setError,
    setValue,
    clearErrors,
  } = useForm<InvoicesManagementModalSchemaType>()

  const clearState = () => {
    setValue('invoiceKeyInput', '')
    clearErrors()
  }

  useEffect(() => {
    clearState()
    setInvoiceKeysList(invoiceKeys)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, invoiceKeys])

  const removeInvoice = (index: number) => {
    setInvoiceKeysList((invoice) => invoice.filter((_invoice, i) => (i !== index)))
  }

  const addInvoiceKey = () => {
    if (isDisabled) return

    const { invoiceKeyInput } = getValues()
    if (invoiceKeyInput === '') {
      setError('invoiceKeyInput', { message: 'Entre com a nota fiscal' })
      return
    }
    if (invoiceKeysList.includes(invoiceKeyInput)) {
      setError('invoiceKeyInput', { message: 'Nota fiscal já adicionada' })
      return
    }

    const onSuccess = () => {
      setInvoiceKeysList((invoice) => [...invoice, invoiceKeyInput])
      clearState()
    }

    setIsDisabled(true)

    dispatch(actions.validateInvoiceKey({
      receivementId: parseInt(receivementId, 10),
      invoiceKey: invoiceKeyInput,
      badge,
      setError: (message) => setError('invoiceKeyInput', { message }),
      onSuccess,
      setIsDisabled,
    }))
  }

  const applyInvoicesManagement = () => {
    if (isDisabled) return

    clearErrors()

    if (invoiceKeysList.length < 1) {
      toast.error('É necessário ter ao menos uma nota fiscal')
      return
    }

    const onSuccess = () => {
      toast.success('Notas fiscais adicionadas/removidas com sucesso')
      setShowModal(false)
    }

    setIsDisabled(true)

    dispatch(actions.applyInvoicesManagement({
      receivementId: parseInt(receivementId, 10),
      invoiceKeys: invoiceKeysList,
      badge,
      onSuccess,
      setIsDisabled,
    }))
  }

  const modalBody = () => (
    <>
      <S.Container>
        {invoiceKeysList.length > 0
      && (
      <InvoicesTable
        invoiceList={invoiceKeysList}
        headerText='Nota Fiscal'
        removeAction={(index: number) => removeInvoice(index)}
        removeDisabled={isDisabled}
      />
      )}
        <S.CustomFieldMui
          onKeyDown={(e) => handleOnKeyDown(e, 'invoiceKeyInput', addInvoiceKey)}
          inputProps={{
            ...register('invoiceKeyInput'),
            'data-testid': 'invoice-key-input-test',
          }}
          label='Bipe ou digite a nota fiscal'
          name='invoiceKeyInput'
          size='small'
          error={!!errors.invoiceKeyInput}
          helperText={errors.invoiceKeyInput?.message}
          autoComplete='off'
          autoFocus
          testId='invoice-key-input-test'
        />
        <S.CustomButtonAdd
          title={
            isDisabled ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <CircularProgress size={20} sx={{ color: '#FFF' }}/>
                <Typography fontSize={14}>Aguarde...</Typography>
              </Box>
            ) : 'Adicionar Nota Fiscal'
          }
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={addInvoiceKey}
          disabled={isDisabled}
          testId='add-invoice-key-button-test'
        />
      </S.Container>
      <S.ModalFooter>
        <S.CustomButtonApply
          title={
            isDisabled ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <CircularProgress size={20} sx={{ color: '#FFF' }}/>
                <Typography fontSize={14}>Aguarde...</Typography>
              </Box>
            ) : 'Aplicar Modificações'
          }
          bgColor='orange3'
          hoverColor='darkOrangeHover'
          letterColor='white'
          onClick={applyInvoicesManagement}
          disabled={isDisabled}
          testId='apply-invoices-management-button-test'
        />
      </S.ModalFooter>
    </>
  )

  return (
    <>
      <S.CustomButton
        title='Gerenciar Notas Fiscais'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        onClick={() => setShowModal(true)}
        testId='invoices-management-button-test'
      />
      <Modal
        show={showModal}
        title='Gerenciar Notas Fiscais'
        body={modalBody()}
        closeButtonAction={() => setShowModal(false)}
        centered
        size='lg'
        testId='invoices-management-modal-test'
      />
    </>
  )
}
